"use client";

// /company/compare — the comparisons, on the company ground.
//
// The company page's own grammar, one section deeper: the same lavender world
// and the organization's red netti, the section ticker (now the LENSES over the
// set — all · portable memory · agent memory · personal ai · notes & knowledge ·
// assistants), the dek, the count, and the frosted cards. Every card wears
// CompareFace: two app icons the same size, "vs" between them, never the
// creature.
//
// Static data in, no fetch: the registry is the truth and the page is built
// once. A lens is a URL (`?lens=agent+memory`) so a link can land on a shelf.

import { useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import OrganismBackdrop from "../OrganismBackdrop";
import SiteMenu, { MENU_ORG, MENU_ORG_SCALE } from "../SiteMenu";
import CompanySearch from "../company/CompanySearch";
import CompanyTicker from "../company/CompanyTicker";
import { useIsNarrow } from "../company/CompanyClient";
import { CO_THEMES } from "../../lib/company/theme";
import { useCoTheme } from "../company/useCoTheme";
import CompareFace from "./CompareFace";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import "../stage/surfaces/compare.css";
import {
  COMPARE_CATEGORIES,
  COMPARE_DEK,
  COMPARE_FAQS,
  type CompareCategory,
  type ComparisonMeta,
} from "../../lib/compare/registry";
import type { MenuSpace } from "../../lib/nav/siteNav";
import { readerOrg, INDEX_ORG_WIDE } from "../../lib/os/readerOrganism";

type Lens = "all" | CompareCategory;

const LENSES: readonly { key: Lens; label: string }[] = [
  { key: "all", label: "compare" },
  ...COMPARE_CATEGORIES.map((c) => ({ key: c.key, label: c.label })),
];

function prettyDate(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).toLowerCase();
}

export default function CompareClient({
  comparisons,
  founder = false,
  initialLens,
}: {
  comparisons: ComparisonMeta[];
  founder?: boolean;
  initialLens?: string;
}) {
  const [lens, setLens] = useState<Lens>(
    LENSES.some((l) => l.key === initialLens) ? (initialLens as Lens) : "all",
  );
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = useIsNarrow();
  // on a phone the creature goes to the bottom centre and back UNDER the veil —
  // there is no field beside the words to stand in (lib/os/readerOrganism).
  const org = readerOrg(isNarrow, INDEX_ORG_WIDE);
  // the compare room — the index keeps one ground of its own; each comparison
  // then takes the other product's colour when you open it.
  const theme = CO_THEMES.compare;
  useCoTheme(theme.ground);

  // law_read_ramp — the same frost the company index wears as you scroll (it
  // hears the stage's scroller; the organism rests under its own calm key)
  useStageReadRamp(220, "art", { calmKey: "compare-index" });

  // law_stage — the head is the stage's navigator: the stage folds it (with the
  // scroll-up reveal); its folding half publishes its natural height.
  const foldRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRef, "--lock-h", lens);

  const selectLens = (key: string) => {
    setLens(key as Lens);
    const qs = key === "all" ? "" : `?lens=${encodeURIComponent(key)}`;
    window.history.replaceState(null, "", `/company/compare${qs}`);
  };

  const list = lens === "all" ? comparisons : comparisons.filter((c) => c.category === lens);
  const dek = lens === "all" ? COMPARE_DEK : COMPARE_CATEGORIES.find((c) => c.key === lens)?.dek ?? COMPARE_DEK;
  const latest = list.map((c) => c.updated).sort().at(-1);

  // the "company" space — this page is one of the company's sections, so the ≡
  // lists its siblings (updates · blog · compare) and the lenses under compare.
  const currentSpace: MenuSpace = {
    id: "company",
    label: "company",
    items: [
      { key: "updates", label: "updates", onSelect: () => { router.push("/company?tab=updates"); } },
      { key: "blog", label: "blog", onSelect: () => { router.push("/company"); } },
      { key: "compare", label: "compare", here: true, onSelect: () => selectLens("all") },
      { key: "about", label: "about", onSelect: () => { router.push("/company/about"); } },
      { key: "team", label: "team", onSelect: () => { router.push("/company/team"); } },
      ...(founder ? [{ key: "inside", label: "inside innernet", onSelect: () => { router.push("/company?tab=inside"); } }] : []),
      ...(founder ? [{ key: "reports", label: "reports", onSelect: () => { router.push("/company?tab=reports"); } }] : []),
    ],
  };

  return (
    <main className="co-page" style={{ ["--bg" as string]: theme.ground } as React.CSSProperties}>
      <OrganismBackdrop
        readingFrost
        fixed
        anchor={menuOpen ? MENU_ORG.anchor : org.anchor}
        offset={menuOpen ? MENU_ORG.offset : org.offset}
        glide={menuOpen ? MENU_ORG.glide : undefined}
        glideAxis={menuOpen ? MENU_ORG.glideAxis : undefined}
        size="min(150vmin, 1520px)"
        blur={0}
        opacity={menuOpen ? MENU_ORG.opacity : 0.97}
        fade={0.88}
        scale={menuOpen ? (isNarrow ? MENU_ORG_SCALE.narrow : MENU_ORG_SCALE.wide) : 1}
        // above the veil, like the dashboard — the scrim frosts the ground, not
        // the creature (z1 veil · z2 netti · z3 the words).
        zIndex={menuOpen ? MENU_ORG.zIndex : org.zIndex}
        innerTransMs={menuOpen ? MENU_ORG.innerTransMs : undefined}
        innerEase={menuOpen ? MENU_ORG.innerEase : undefined}
        color={theme.color}
        age={theme.age}
        spin
        publishHue
        live={false}
      />

      <div className="co-read-scrim" aria-hidden />

      <SiteMenu
        trail={[{ label: "company", href: "/company" }, { label: "compare" }]}
        founder={founder}
        current={currentSpace}
        hereKey="company"
        search={<CompanySearch />}
        onOpenChange={setMenuOpen}
      />

      {/* law_stage — the lenses are the stage's navigator, over the cards' own
          scroll area. A lens is a SHELF of one set, not a place of its own (it
          replaces the url, it does not push), so the scene stays one scene. */}
      <Stage
        frame="none"
        autoFold
        top="clamp(4.6rem, 11vh, 6.4rem)"
        pin="clamp(4.2rem, 8.6vh, 4.75rem)"
        gap="clamp(1.6rem, 4vh, 2.6rem)"
        headClassName="os-lock--detached"
        sceneKey="compare"
        dir={1}
        label="comparisons"
        rootProps={{ className: "stage--compare" }}
        head={
          <div className="co-shell stage-compare__head">
            <header className="co-head co-head--menu">
              <CompanyTicker tabs={LENSES} active={lens} onSelect={selectLens} />
              <div ref={foldRef} className="os-lock__fold co-head__fold">
                <p className="co-dek">{dek}</p>
                <div className="co-meta">
                  <span>{list.length} {list.length === 1 ? "comparison" : "comparisons"}</span>
                  {latest && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.32rem" }}>
                      <span className="ink-dot" aria-hidden />
                      <b>checked {prettyDate(latest)}</b>
                    </span>
                  )}
                </div>
              </div>
            </header>
          </div>
        }
      >
        <div className="co-shell stage-compare__body">
          <div className="co-body">
            <div className="co-grid">
              {list.map((c, i) => (
                <Link
                  key={c.slug}
                  className="co-card cmp-card os-enter"
                  href={c.href}
                  style={{ ["--i" as string]: Math.min(i, 8) }}
                >
                  <span className="co-card__media cmp-card__media">
                    <CompareFace left={c.left} right={c.right} size={isNarrow ? 56 : 64} />
                  </span>
                  <span className="co-card__t">{c.title}</span>
                  <span className="co-card__d">{c.dek}</span>
                  <span className="co-by">
                    <span>{c.category}</span>
                    <span className="ink-dot" aria-hidden />
                    <span>{prettyDate(c.updated)}</span>
                  </span>
                </Link>
              ))}
            </div>

            {/* the answers — the same five pairs the page hands search engines as
                FAQPage, read here by a person. Plain prose, no accordion: a
                question with its answer under it is the shape both of them want. */}
            <section className="cmp-faq" aria-labelledby="cmp-faq-h">
              <hr className="share-rule" aria-hidden />
              <h2 id="cmp-faq-h" className="cmp-faq__h">the short answers</h2>
              {COMPARE_FAQS.map((f) => (
                <div key={f.q} className="cmp-faq__item">
                  <h3 className="cmp-faq__q">{f.q}</h3>
                  <p className="cmp-faq__a">{f.a}</p>
                </div>
              ))}
              <p className="cmp-faq__foot">
                every comparison is checked against the other product’s public site on the date it carries. if we’ve
                described someone unfairly, <a className="quiet-link" href="mailto:connect@innernet.live">tell us</a> and
                we’ll correct it.
              </p>
            </section>
          </div>
        </div>
      </Stage>
    </main>
  );
}
